import {z} from "zod";
import {record, text, type Row} from "../dataPipeline/model";
export const scalar = (value: unknown, fallback = "") =>
    typeof value === "string" || typeof value === "number" || typeof value === "boolean" ? String(value) : fallback;
export type TemplateKind = "data-source-templates" | "data-cluster-templates";
export const titleFor = (kind: TemplateKind) => (kind === "data-source-templates" ? "Data source templates" : "Data cluster templates");
export const singularFor = (kind: TemplateKind) => (kind === "data-source-templates" ? "data source template" : "data cluster template");
export const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function defaults(kind: TemplateKind): Row {
    const base = {id: crypto.randomUUID(), displayName: "", description: "", isActive: true};
    return kind === "data-source-templates"
        ? {
              ...base,
              name: "",
              connectionStrategy: "71c14b30-05e7-4edd-a667-9bef5a0ddedf",
              imageId: "7e82718b-245b-4ac2-aca2-ad8cd935caef",
              sourceCredentialsSchema: [],
              tags: [],
              companies: null,
          }
        : {
              ...base,
              dataType: "PERIODIC",
              dataPipeline: "",
              pipelineBackend: "IRIS",
              maxRetries: 3,
              thresholdDays: 30,
              refreshDuration: 7,
              attributionWindow: 0,
              timeout: null,
              imageId: null,
              syncConfig: null,
              tableSchema: {data: [{id: crypto.randomUUID(), internalName: "date", columnName: "Date", columnType: "date"}]},
              dataSourceTemplateIds: [],
              datasetTemplateIds: [],
          };
}
export function parseJsonField(value: string, label: string): unknown {
    try {
        return JSON.parse(value);
    } catch {
        throw new Error(`${label} must contain valid JSON.`);
    }
}
export function numericField(value: unknown, label: string, minimum: number, nullable = false): number | null {
    if ((value === "" || value === null || value === undefined) && nullable) {
        return null;
    }
    if (typeof value !== "number" && typeof value !== "string") {
        throw new Error(`${label} must be an integer.`);
    }
    const n = Number(value);
    if (value === "" || !Number.isSafeInteger(n) || n < minimum || n > 2147483647) {
        throw new Error(`${label} must be an integer between ${minimum} and 2147483647.`);
    }
    return n;
}
export function templatePayload(kind: TemplateKind, draft: Row, schemas: Record<string, string>, supported: string[]): Row {
    const payload: Row = {
        id: draft.id,
        revision: draft.revision,
        displayName: draft.displayName,
        description: text(draft, "description", "").length > 0 ? draft.description : null,
        isActive: draft.isActive,
    };
    if (!uuidPattern.test(String(draft.id))) {
        throw new Error("Template ID must be a UUID.");
    }
    if (!text(draft, "displayName", "").trim()) {
        throw new Error("Display name is required.");
    }
    if (kind === "data-source-templates") {
        Object.assign(payload, {
            name: draft.name,
            connectionStrategy: draft.connectionStrategy,
            imageId: draft.imageId,
            sourceCredentialsSchema: parseJsonField(schemas.sourceCredentialsSchema ?? "[]", "Credential schema"),
        });
        if (supported.includes("tags")) {
            payload.tags = text(draft, "tagsText", Array.isArray(draft.tags) ? draft.tags.filter((t): t is string => typeof t === "string").join(", ") : "")
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);
        }
        if (supported.includes("companies")) {
            payload.companies = parseJsonField(schemas.companies ?? "null", "Workspace allowlist");
        }
    } else {
        Object.assign(payload, {
            tableSchema: parseJsonField(schemas.tableSchema ?? "{}", "Table schema"),
            dataType: draft.dataType,
            dataPipeline: draft.dataPipeline,
            pipelineBackend: draft.pipelineBackend,
            maxRetries: numericField(draft.maxRetries, "Maximum retries", 0),
            attributionWindow: numericField(draft.attributionWindow, "Attribution window", 0),
            thresholdDays: numericField(draft.thresholdDays, "Threshold days", 0, true),
            refreshDuration: numericField(draft.refreshDuration, "Refresh duration", 1, true),
            timeout: numericField(draft.timeout, "Timeout", 1, true),
            imageId: text(draft, "imageId", "").length > 0 ? draft.imageId : null,
            dataSourceTemplateIds: draft.dataSourceTemplateIds,
            datasetTemplateIds: draft.datasetTemplateIds,
        });
        if (supported.includes("sync_config")) {
            payload.syncConfig = parseJsonField(schemas.syncConfig ?? "null", "Sync configuration");
        }
    }
    return record(payload);
}

export const templateSearchSchema = z
    .object({mode: z.enum(["list", "view", "create", "edit"]).default("list"), id: z.string().uuid().optional()})
    .refine((value) => !(value.mode === "view" || value.mode === "edit") || value.id !== undefined, {message: "Choose a template to view or edit"});
export function templateUrl(kind: TemplateKind, mode: "list" | "view" | "create" | "edit" = "list", id?: string): string {
    const query = new URLSearchParams();
    if (mode !== "list") {
        query.set("mode", mode);
    }
    if (id !== undefined) {
        query.set("id", id);
    }
    return `/a/${kind}${query.size > 0 ? `?${query}` : ""}`;
}
